import React from 'react';
import { TextInput } from '../../../shared/components/TextInput';
import type { JobOpening } from '../api';
import { WORK_MODEL_LABELS, formatClienteLabel } from './format';

/** Cargo da vaga: título público, ou o cargo da requisição enquanto não há anúncio. */
export function cargoDaVaga(vaga: JobOpening): string {
  return vaga.title?.trim() || vaga.requisition?.positionTitle || '';
}

function nomeDoCliente(vaga: JobOpening): string | undefined {
  const client = vaga.requisition?.client;
  return client?.nomeFantasia || client?.razaoSocial || undefined;
}

/**
 * Rótulo da vaga em seletores: ID + cargo + cliente.
 *
 * O mesmo cargo existe para clientes, cidades e modalidades diferentes;
 * é o ID que identifica a vaga, e o cliente no rótulo permite buscá-la
 * por ele também (o filtro do seletor olha só o rótulo).
 */
export function vagaOptionLabel(vaga: JobOpening): string {
  const codigo = vaga.requisition?.code;
  const cargo = cargoDaVaga(vaga) || 'Vaga sem título';
  const cliente = nomeDoCliente(vaga);
  return [codigo, cargo].filter(Boolean).join(' — ') + (cliente ? ` · ${cliente}` : '');
}

/** Texto secundário da opção: cidade e modalidade. */
export function vagaOptionDescription(vaga: JobOpening): string | undefined {
  const modalidade = vaga.workModel ? WORK_MODEL_LABELS[vaga.workModel] : undefined;
  return [vaga.city, modalidade].filter(Boolean).join(' · ') || undefined;
}

/**
 * Dados que a candidatura herda da vaga pretendida: ID, cargo, cliente e
 * modalidade.
 *
 * Vêm do cadastro da vaga/requisição e por isso ficam somente leitura —
 * para mudar qualquer um, altera-se a vaga, não o candidato. Sem vaga
 * escolhida, os campos aparecem vazios. Renderiza as células soltas para
 * o formulário posicioná-las no próprio grid.
 */
export function VagaVinculadaCampos({ vaga }: { vaga: JobOpening | null }) {
  const requisicao = vaga?.requisition;
  return (
    <>
      <TextInput label="ID da vaga" value={requisicao?.code ?? '—'} disabled readOnly />
      <TextInput label="Cargo" value={(vaga && cargoDaVaga(vaga)) || '—'} disabled readOnly />
      <TextInput
        label="Cliente"
        value={requisicao?.client ? formatClienteLabel(requisicao.client) : '—'}
        disabled
        readOnly
      />
      <TextInput
        label="Modalidade"
        value={vaga?.workModel ? WORK_MODEL_LABELS[vaga.workModel] : '—'}
        disabled
        readOnly
      />
    </>
  );
}
